"use client";

import {
  LoginButtonConfig,
  LoginGuideContentProps,
  LoginGuideContentRef,
} from "./types";
import { LoginStore, rootStore } from "@/src/store";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import type { Web3LoginSupportedType } from "@/src/features/auth/compat/sdk-types";
import { useOspLogin } from "@/src/features/auth/compat/useOspLogin";

import { FeatureList } from "./FeatureList";
import { I18nUtil } from "@utils/i18n";
import { LoginButtons } from "./LoginButtons";
import { LoginGuideFeatures } from "../../GuidePopup/constants";
import NextImage from "../../NextImage";
import { OspToast } from "@ospweb/sdk-ui";
import { TermsAgreement } from "./TermsAgreement";
import Translations from "@translate/components/LoginGuidePopup/LoginGuideContent/LoginGuideContent";
import { cn } from "utils";
import log from "logbeacon";
import { useLoginGuideContent } from "@/src/hooks/useLoginGuideContent";
import { useSnapshot } from "valtio";

const I18n = I18nUtil.createScoped(Translations);

const PRIMARY_LOGIN_BUTTONS: LoginButtonConfig[] = [
  {
    provider: "APPLE",
    label: "Login with Apple",
    enabled: true,
    isPrimary: true,
    iconName: "apple",
  },
  {
    provider: "GOOGLE",
    label: "Login with Google",
    enabled: true,
    isPrimary: false,
    iconName: "Google with colour",
  },
];

const MORE_LOGIN_BUTTONS: LoginButtonConfig[] = [];

/**
 * LoginGuideContent 组件
 *
 * 登录引导内容组件，采用左右布局设计
 * 左侧：品牌标识、功能描述、特性列表
 * 右侧：登录选项、条款同意
 *
 * @example
 * ```tsx
 * <LoginGuideContent
 *   onClose={() => console.log('关闭')}
 *   onLogin={(provider) => console.log('登录:', provider)}
 * />
 * ```
 */
export const LoginGuideContent = forwardRef<
  LoginGuideContentRef,
  LoginGuideContentProps
>(({ className }, _ref) => {
  const { login, error, isLoading } = useOspLogin();
  const { type } = useSnapshot(rootStore.app);
  // 使用业务逻辑 Hook
  const { termsAccepted, onTermsChange, subLoginType, setSubLoginType } =
    useLoginGuideContent();

  // 社交登录点击反馈 loading：点击后立即显示，5s 后自动清除，不影响业务 isLoading
  const [isSocialClickLoading, setIsSocialClickLoading] = useState(false);
  const socialClickTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (error) {
      setIsSocialClickLoading(false);
      if (socialClickTimerRef.current) {
        clearTimeout(socialClickTimerRef.current);
      }
      OspToast.error({
        content: I18n.t("Login failed, please try again."),
      });
    }
  }, [error]);

  useEffect(() => {
    if (isLoading) {
      log.debug("[login guide popup] ==== isLoading ==== ", isLoading);
      LoginStore.isLoading = isLoading;
    }

    if (type !== "extension" && isLoading === false) {
      log.debug(
        "[login guide popup] ==== isBusinessLoading === auto login finish",
      );
      LoginStore.isLoading = false;
      if (isSocialClickLoading) {
        setIsSocialClickLoading(false);
        if (socialClickTimerRef.current) {
          clearTimeout(socialClickTimerRef.current);
        }
      }
    }
  }, [isLoading, type]);

  const loginController = async (type: Web3LoginSupportedType) => {
    try {
      log.info("[login guide popup] ==== loginController ==== ", type);
      setSubLoginType(type);
      // 按钮点击即时反馈：显示 loading，5s 后自动清除
      setIsSocialClickLoading(true);
      if (socialClickTimerRef.current) {
        clearTimeout(socialClickTimerRef.current);
      }
      socialClickTimerRef.current = setTimeout(() => {
        setIsSocialClickLoading(false);
      }, 5000);
      login(type);
    } catch (error) {
      log.error("[LoginGuideContent] loginController error", error);
    }
  };

  const features = LoginGuideFeatures();

  return (
    <div
      className={cn(
        "flex flex-row relative w-full h-full justify-between",
        className,
      )}
    >
      {/* 左侧内容区域 */}
      <div className="flex-1 flex flex-col gap-4 pr-0">
        {/* Logo 和描述 */}
        <div className="flex flex-col gap-2">
          {/* Logo */}
          <picture>
            <source
              srcSet="/assets/images/edgen-logo.svg"
              type="image/svg+xml"
              media="(min-width: 960px)"
            />
            <NextImage
              src="/assets/images/edgen-logo-dark.svg"
              alt="edgen logo"
              width={150}
              height={42}
            />
          </picture>

          {/* 描述文本 */}
          <div className="body_m text-content_secondary">
            {I18n.t(
              "Get more personalized analysis of stocks or cryptocurrencies.",
            )}
          </div>
        </div>

        {/* 功能特性列表 */}
        <FeatureList features={features} />
      </div>
      {/* 右侧内容区域 */}
      <div className="flex-1 flex flex-col gap-4 border-l border-border_white_transparent10 px-6 ">
        {/* 标题 */}
        <p className="headline_regular_s text-content_primary">
          {I18n.t("Login / Sign up to Edgen")}
        </p>

        {/* 登录按钮组 */}
        <LoginButtons
          buttons={PRIMARY_LOGIN_BUTTONS}
          moreButtons={MORE_LOGIN_BUTTONS}
          onClickLogin={loginController}
          termsAccepted={termsAccepted}
          subLoginType={subLoginType}
          loading={isLoading || isSocialClickLoading}
        />

        {/* 条款同意 */}
        <TermsAgreement
          termsAccepted={termsAccepted}
          onTermsChange={onTermsChange}
        />
      </div>
    </div>
  );
});

LoginGuideContent.displayName = "LoginGuideContent";
